import { POSROI_BASE } from "../utils/apiConfig";

export type LiveVehicle = {
  id: string;
  lat: number;
  lng: number;
  bearing?: number;
  lineId?: string | number;
};

export type MapBounds = {
  south: number;
  west: number;
  north: number;
  east: number;
};

const FLAT_KEYS = [
  "groupLineNrReal",
  "presentationLineNr",
  "lineNrReal",
  "runNo",
  "startStopId",
  "nextStopId",
  "nextStopName",
  "nextStopDepartureTimeText",
  "lat",
  "lng",
  "angle",
  "specialGraphics",
] as const;
const FLAT_STEP = FLAT_KEYS.length;

type FlatVehicle = Record<(typeof FLAT_KEYS)[number], any>;

const normLine = (x: unknown) =>
  String(x ?? "")
    .trim()
    .toLowerCase();

const buildBoundsParam = (b: MapBounds) =>
  `${b.south},${b.west},${b.north},${b.east}`;

const toNum = (n: unknown): number | undefined => {
  if (n == null) return undefined;
  if (typeof n === "number" && Number.isFinite(n)) return n;
  if (typeof n === "string") {
    const v = Number(n.replace(",", "."));
    return Number.isFinite(v) ? v : undefined;
  }
  return undefined;
};

const isFlatChunk = (a: any[]): boolean =>
  Array.isArray(a) &&
  a.length >= FLAT_STEP &&
  a.every((x) => x === null || typeof x !== "object");

function interpretFlatVehiclesFiltered(
  arr: any[],
  lineFilter?: string | number
): FlatVehicle[] {
  const out: FlatVehicle[] = [];

  for (let i = 0; i + FLAT_STEP - 1 < arr.length; i += FLAT_STEP) {
    const v: any = {};
    for (let j = 0; j < FLAT_STEP; j++) v[FLAT_KEYS[j]] = arr[i + j];
    out.push(v as FlatVehicle);
  }
  return out;
}

function mapOneVehicleExplicit(raw: any): LiveVehicle | null {
  if (!raw || typeof raw !== "object") return null;

  const latRaw = raw.lat ?? raw.Lat ?? raw.latitude ?? raw.Latitude;
  const lngRaw =
    raw.lng ?? raw.Lng ?? raw.lon ?? raw.Lon ?? raw.longitude ?? raw.Longitude;
  const lat = toNum(latRaw);
  const lng = toNum(lngRaw);
  if (lat === undefined || lng === undefined) return null;

  // Line ID variants
  const lineIdRaw =
    raw.groupLineNrReal ??
    raw.lineNrReal ??
    raw.presentationLineNr ??
    raw.lineId ??
    raw.LineId ??
    raw.line ??
    raw.Line ??
    raw.lineno ??
    raw.LineNo ??
    raw.LineNr ??
    raw.LineNrReal ??
    raw.lineNrReal ??
    raw.groupLineNrReal;

  // Vehicle ID/run number variants
  const idRaw =
    raw.runNo ??
    raw.RunNo ??
    raw.id ??
    raw.Id ??
    `${lat.toFixed(5)},${lng.toFixed(5)}`;

  // Bearing/heading variants
  const bearingRaw =
    raw.bearing ??
    raw.Bearing ??
    raw.heading ??
    raw.Heading ??
    raw.angle ??
    raw.Angle;
  const bearing = toNum(bearingRaw);

  // Require at least one form of ID
  if (
    lineIdRaw == null &&
    raw.runNo == null &&
    raw.RunNo == null &&
    raw.id == null &&
    raw.Id == null
  ) {
    return null;
  }

  return {
    id: String(idRaw),
    lat,
    lng,
    bearing: bearing ?? undefined,
    lineId: lineIdRaw != null ? String(lineIdRaw) : undefined,
  };
}

// Strict line filter
const matchesLineStrict = (v: LiveVehicle, filter?: string | number) =>
  filter == null || `${filter}`.trim() === ""
    ? true
    : normLine(v.lineId) === normLine(filter);

const dedupeVehicles = (list: LiveVehicle[]) => {
  const seen = new Set<string>();
  return list.filter((v) => {
    const key = `${normLine(v.lineId)}::${v.id}::${v.lat.toFixed(
      5
    )}::${v.lng.toFixed(5)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// Main fetch function
export async function fetchLiveVehiclesByBounds(
  bounds: MapBounds,
  lineFilter?: string | number
): Promise<LiveVehicle[]> {
  const params = new URLSearchParams();
  params.set("bounds", buildBoundsParam(bounds));

  if (lineFilter != null && `${lineFilter}`.trim() !== "") {
    params.set("filterInBounds", String(lineFilter));
    params.set("filterNoBounds", "");
  }

  const url = `${POSROI_BASE}/get?${params.toString()}`;

  if (process.env.NODE_ENV === "development") {
    console.debug("[live] calling", url);
  }

  const res = await fetch(url, { headers: { Accept: "application/json" } });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Live API ${res.status}: ${text || res.statusText}`);
  }

  const data = await res.json();

  let arr: any[] = [];
  if (Array.isArray((data as any)?.vehicles)) arr = (data as any).vehicles;
  else if (Array.isArray((data as any)?.items)) arr = (data as any).items;
  else if (Array.isArray((data as any)?.data)) arr = (data as any).data;
  else if (Array.isArray((data as any)?.results)) arr = (data as any).results;
  else if (Array.isArray(data) && data.length && typeof data[0] === "object")
    arr = data;
  else if (Array.isArray(data) && isFlatChunk(data))
    arr = interpretFlatVehiclesFiltered(data, lineFilter);

  // Map and filter
  const mapped = arr.flatMap((it) => {
    try {
      const m = mapOneVehicleExplicit(it);
      return m ? [m] : [];
    } catch {
      return [];
    }
  });

  const finalList = dedupeVehicles(
    lineFilter != null && `${lineFilter}`.trim() !== ""
      ? mapped.filter((v) => matchesLineStrict(v, lineFilter))
      : mapped
  );

  if (process.env.NODE_ENV === "development") {
    console.debug("[live] parsed vehicles (ultra-slim)", {
      requestedLine: lineFilter,
      totalRaw: Array.isArray(arr) ? arr.length : 0,
      vehicles: finalList.length,
      sample: finalList[0],
    });
  }

  return finalList;
}

// Fetch vehicles for current map bounds
export async function fetchLiveVehiclesForLine(
  map: google.maps.Map,
  lineId?: string | number
): Promise<LiveVehicle[]> {
  const b = map.getBounds();
  if (!b) return [];

  const bounds: MapBounds = {
    south: b.getSouthWest().lat(),
    west: b.getSouthWest().lng(),
    north: b.getNorthEast().lat(),
    east: b.getNorthEast().lng(),
  };

  return fetchLiveVehiclesByBounds(bounds, lineId);
}
